import type { MixAndMatchInfo } from '~/types/Product';

interface MixAndMatchCartLine {
  mixAndMatch?: Nullable<MixAndMatchInfo>;
  count: number;
  /** Unit price charged by the last cart calculation; 0 without a discount. */
  discountPrice: number;
}

interface EligibleLine {
  promo: MixAndMatchInfo;
  count: number;
  paid: number;
}

export interface MixAndMatchProgress {
  threshold: number;
  eligibleUnits: number;
  /** Units still missing; 0 once unlocked. */
  remaining: number;
  isUnlocked: boolean;
  /** Saving over the regular prices, minor units: expected while locked, charged once unlocked. */
  savings: number;
}

/** Units in the cart that count toward the Mix & Match threshold. */
export function countMixAndMatchUnits(
  lines: Pick<MixAndMatchCartLine, 'mixAndMatch' | 'count'>[],
): number {
  return lines.reduce(
    (sum, { mixAndMatch, count }) => (mixAndMatch ? sum + count : sum),
    0,
  );
}

/** Highest threshold among the lines that take part; 0 when none does. */
export function getMixAndMatchThreshold(
  lines: Pick<MixAndMatchCartLine, 'mixAndMatch'>[],
): number {
  return lines.reduce(
    (max, { mixAndMatch }) => Math.max(max, mixAndMatch?.threshold ?? 0),
    0,
  );
}

/** Slack for the catalogue and OMS converting the promo price to the display currency separately. */
const PROMO_PRICE_TOLERANCE = 0.01;

const isChargedAtPromoPrice = ({ promo, paid }: EligibleLine): boolean =>
  paid > 0 && paid <= promo.price * (1 + PROMO_PRICE_TOLERANCE);

/** Whether the last cart calculation charged this line at its promo price. */
export function isLineChargedAtPromoPrice({
  mixAndMatch,
  discountPrice,
}: Pick<MixAndMatchCartLine, 'mixAndMatch' | 'discountPrice'>): boolean {
  return (
    !!mixAndMatch &&
    isChargedAtPromoPrice({ promo: mixAndMatch, count: 0, paid: discountPrice })
  );
}

/**
 * Mix & Match state of the cart: units of every eligible line count toward one
 * threshold regardless of category. Past it, only lines OMS charged at the promo
 * price count as unlocked; null when there are none, e.g. an order split
 * between warehouses.
 */
export function getMixAndMatchProgress(
  lines: MixAndMatchCartLine[],
): MixAndMatchProgress | null {
  const eligible = lines.flatMap(
    ({ mixAndMatch, count, discountPrice }): EligibleLine[] =>
      mixAndMatch && count > 0
        ? [{ promo: mixAndMatch, count, paid: discountPrice }]
        : [],
  );

  if (!eligible.length) {
    return null;
  }

  const threshold = Math.max(...eligible.map(({ promo }) => promo.threshold));
  const eligibleUnits = countMixAndMatchUnits(lines);

  if (eligibleUnits < threshold) {
    return {
      threshold,
      eligibleUnits,
      remaining: threshold - eligibleUnits,
      isUnlocked: false,
      savings: eligible.reduce(
        (sum, { promo, count }) =>
          sum + (promo.regularPrice - promo.price) * count,
        0,
      ),
    };
  }

  const charged = eligible.filter(isChargedAtPromoPrice);

  if (!charged.length) {
    return null;
  }

  return {
    threshold,
    eligibleUnits,
    remaining: 0,
    isUnlocked: true,
    savings: charged.reduce(
      (sum, { promo, count, paid }) =>
        sum + Math.max(0, promo.regularPrice - paid) * count,
      0,
    ),
  };
}
